<template>
	<div class="downgrade-survey">
		<Callout
			v-if="viewModel && viewModel.CurrentPlanIsLegacyPricingTier && !viewModel.HasPermanentPricingOverride"
			type="warning"
		>
			{{ useNewBilling ? 'This change schedules your downgrade for the end of your current billing period. It could result in reduced form functionality and loss of access to legacy (lower) pricing. Are you sure you want to schedule this downgrade?' : 'Downgrading could result in reduced form functionality and loss of access to legacy (lower) pricing. Are you sure you want to downgrade?' }}
		</Callout>
		<Callout
			v-else
			type="warning"
		>
			{{ useNewBilling ? 'This change schedules your downgrade for the end of your current billing period. Downgrading your plan could cause a loss of form data and functionality. Please confirm your new plan has the data storage and functions your forms require.' : 'Downgrading your plan could cause a loss of form data and functionality. Please confirm your new plan has the data storage and functions your forms require.' }}
		</Callout>
		<fieldset>
			<legend class="font-bold mt-4 mb-3">{{ useNewBilling ? 'Let us know why you are scheduling this downgrade' : 'Let us know why you are downgrading' }}</legend>
			<CheckboxGroup
				:options="downgradeReasonOptions"
				:value="selectedReasons"
				@change="selectedReasonsUpdated"
			/>
		</fieldset>
		<Input
			id="comments"
			data-testid="comments"
			type="textarea"
			label="Comments"
			@change="(value) => comments = value"
		/>
		<div class="downgrade-survey__buttons flex justify-end gap-3">
			<Button
				class="button--secondary"
				@click="cancel"
			>
				Cancel
			</Button>
			<StatusButton
				:status="internalButtonStatus"
				class="button--primary"
				@click="submit"
			>
				{{ useNewBilling ? 'Schedule Downgrade' : 'Downgrade' }}
			</StatusButton>
		</div>
	</div>
</template>

<script lang="ts">
	import type { PropType } from 'vue';
	import { defineComponent, ref } from 'vue';
	import Callout from 'src/components/content/Callout.vue';
	import CheckboxGroup from 'src/components/form-controls/CheckboxGroup.vue';
	import Input from 'src/components/form-controls/Input.vue';
	import Button from 'src/components/interaction/Button.vue';
	import StatusButton from 'src/components/interaction/StatusButton.vue';
	import type { ButtonStatus } from 'src/components/interaction/StatusButton.vue';
	import type { DowngradeFeedbackViewModel } from '@cognitoforms/types/server-types/services/view-models/downgrade-feedback-view-model';

	export default defineComponent({
		components: {
			Callout,
			CheckboxGroup,
			Input,
			StatusButton,
			Button
		},
		props: {
			buttonStatus: { type: String as PropType<ButtonStatus>, default: 'disabled' },
			useNewBilling: { type: Boolean, default: false },
			viewModel: { type: Object as PropType<DowngradeFeedbackViewModel>, default: null }
		},
		setup(props) {
			const downgradeReasonOptions = [
				{ value: 'I didn\'t use my plan\'s premium features' },
				{ value: 'I only need it seasonally/my project ended' },
				{ value: 'Too difficult to learn/use premium features' },
				{ value: 'It\'s too expensive' },
				{ value: 'Support content wasn\'t helpful' },
				{ value: 'I found a better solution' }
			];

			const selectedReasons = ref<Array<string>>([]);
			const comments = ref('');

			const internalButtonStatus = ref<ButtonStatus>(props.buttonStatus);

			return {
				downgradeReasonOptions,
				selectedReasons,
				internalButtonStatus,
				comments
			};
		},
		watch: {
			buttonStatus() {
				this.internalButtonStatus = this.buttonStatus;
			}
		},
		methods: {
			selectedReasonsUpdated(newReasons) {
				this.selectedReasons = newReasons;

				if (this.selectedReasons.length)
					this.internalButtonStatus = 'default';
				else
					this.internalButtonStatus = 'disabled';
			},
			submit() {
				this.$emit('submit', { downgradeReasons: this.selectedReasons, comments: this.comments });
			},
			cancel() {
				this.$router.replace({ name: 'organization-settings-plan' });
			}
		}
	});
</script>

<style lang="scss" src="@cognitoforms/styles/app/common/_checkables-legacy.scss"></style>
<style lang="scss">
.downgrade-survey {

	.button--has-status .button__text {
		opacity: 1;
	}

	[data-app-layout] ~ .el-dialog__wrapper &__buttons {
		position: absolute;
		bottom: $space__5;
		right: $space__5;
	}
}
</style>